import { GroupedRoute } from "./types/transitTypes";

// Utility functions for processing transit route data

// Filters out latitude/longitude fields and prunes empty objects from route data
export const filterLatLong = (obj: any): any => {
  if (Array.isArray(obj)) {
    const filteredArray = obj
      .map((item) => filterLatLong(item))
      .filter(
        (item) =>
          item !== undefined &&
          (typeof item !== "object" ||
            Object.keys(item).length > 0 ||
            Array.isArray(item)),
      );
    return filteredArray.length > 0 ? filteredArray : undefined;
  }
  if (obj !== null && typeof obj === "object") {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (
        key.toLowerCase() === "lat" ||
        key.toLowerCase() === "lng" ||
        key.toLowerCase() === "longitude" ||
        key.toLowerCase() === "latitude"
      ) {
        continue;
      }
      const filteredValue = filterLatLong(value);
      if (filteredValue !== undefined) {
        newObj[key] = filteredValue;
      }
    }
    if (
      Object.keys(newObj).length === 0 &&
      (obj.steps ||
        obj.transit ||
        obj.duration ||
        obj.distance ||
        obj.arrival_time ||
        obj.departure_time)
    ) {
      return obj;
    }
    return Object.keys(newObj).length > 0 ? newObj : undefined;
  }
  return obj;
};

// Checks if two routes are identical, ignoring times
export const areRoutesIdentical = (
  route1: google.maps.DirectionsRoute,
  route2: google.maps.DirectionsRoute,
): boolean => {
  if (!route1?.legs || !route2?.legs) return false;
  if (route1.legs.length !== route2.legs.length) return false;

  for (let i = 0; i < route1.legs.length; i++) {
    const leg1 = route1.legs[i];
    const leg2 = route2.legs[i];
    if (leg1.steps.length !== leg2.steps.length) return false;

    for (let j = 0; j < leg1.steps.length; j++) {
      const step1 = leg1.steps[j];
      const step2 = leg2.steps[j];
      if (step1.travel_mode !== step2.travel_mode) {
        return false;
      }
      // Use string comparison for compatibility with REST API response
      const travelMode1 = step1.travel_mode as string;
      if (travelMode1 === "TRANSIT") {
        if (
          step1.transit?.line?.short_name !== step2.transit?.line?.short_name ||
          step1.transit?.headsign !== step2.transit?.headsign ||
          step1.transit?.departure_stop?.name !==
            step2.transit?.departure_stop?.name ||
          step1.transit?.arrival_stop?.name !==
            step2.transit?.arrival_stop?.name
        ) {
          return false;
        }
      } else if (step1.instructions !== step2.instructions) {
        return false;
      }
    }
  }
  return true;
};

// Groups identical routes and extracts time options
export const groupIdenticalRoutes = (
  routes: google.maps.DirectionsRoute[],
): GroupedRoute[] => {
  const groupedRoutes: GroupedRoute[] = [];
  const routeGroups: { route: google.maps.DirectionsRoute; times: any[] }[] =
    [];

  routes.forEach((route) => {
    const matchingGroup = routeGroups.find((group) =>
      areRoutesIdentical(group.route, route),
    );

    // Use string comparison for compatibility with REST API response
    const times = route.legs.flatMap((leg: any) =>
      leg.steps
        .filter((step: any) => (step.travel_mode as string) === "TRANSIT")
        .map((step: any) => ({
          departureTime: step.transit?.departure_time?.text || "unknown time",
          arrivalTime: step.transit?.arrival_time?.text || "unknown time",
          duration: step.duration?.text || "unknown duration",
        })),
    );

    if (matchingGroup) {
      matchingGroup.times.push(...times);
    } else {
      routeGroups.push({ route, times });
    }
  });

  return routeGroups.map((group) => ({
    ...group.route,
    timeOptions: group.times,
  }));
};
